import { BREAKPOINTS } from "../../config/breakpoints";
import React, { useEffect, useState, useRef } from "react";
import { Table, Thead, Trow, Tcol, Tbody } from "../../Generic/Table";
import styled from "styled-components";
import { search } from "../../util/util";
import { errorNotification } from "../../Generic/Notifications";
import Loader from "../../Generic/Loader";
import EmptyState from "../../Generic/EmptyState";
import RecipeCreate from "./RecipeCreate";
import StudioService from "../Services/StudioService";
import { useParams, useNavigate } from "react-router-dom";
import { useRecipe } from "./context/RecipeContext";
import { formatPreparationTime } from "../Utils/RecipeUtil";
import { zIndex } from "../../config/zIndex";

const Page = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1rem;
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	justify-content: center;
`;

const Div = styled.div`
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	flex-wrap: ${(props) => props.flexWrap};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	font-family: ${(props) => props.fontFamily};
	@media (max-width: ${BREAKPOINTS.desktop}px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

const RecipeList = () => {
	const { recipes, getRecipes, getMoreRecipes, Patterns } = useRecipe();
	const [searchParams, setSearchParams] = useState(["_id"]);
	const [query, setQuery] = useState("");
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [lastElement, setLastElement] = useState();
	const [create, setCreateMode] = useState(false);
	const navigate = useNavigate();
	const hasMore = useRef(true);

	async function fetchRecipes() {
		setLoading(true);

		const response = await StudioService.getRecipes({ pageNumber: page, nPerPage: 10 });
		const { data, error } = response;
		if (data) {
			const { recipeList, count } = data;
			getRecipes(recipeList);
			setLoading(false);
			setError(false);

			if (count <= recipes.length) {
				hasMore.current = false;
			}

		} else if (error) {
			setLoading(false);
			setError(true);
			errorNotification(error);
		}
	}

	//Creating a new observer which increments the page number once the observed element is fully visible
	const observer = useRef(
		new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					if (hasMore.current) {
						//Getting the earlier state and incrementing it
						setPage((page) => page + 1);
					}
				}
			},
			{
				//Threshold set to 1 for the page to increment only when the element is 100% visible
				//Threshold = % of element to be visible / 100
				threshold: 1,
			}
		)
	);

	useEffect(() => {
		//Fires everytime page changes
		fetchRecipes();
	}, [page]);

	useEffect(() => {
		//Update the element on which the call is made and set the observer on it once the list re-renders
		const currentElement = lastElement;
		const currentObserver = observer.current;

		if (currentElement) {
			currentObserver.observe(currentElement);
		}

		//Remove the observer once user exits the page
		return () => {
			if (currentElement) {
				currentObserver.unobserve(currentElement);
			}
		};
	}, [lastElement]);

	useEffect(() => {
		return () => {
			getRecipes([]);
		};
	}, [getRecipes]);

	return (
		<Page>
			<Div background="#F2F2F2" zIndex={zIndex.appHeader} top="0" padding="0" alignItem="center" width="100%" jContent="space-between">
				<Header>Recipes</Header>
			</Div>
			{!error && (
				<Table margin="0rem 0 0 0">
					<Trow head top="0">
						<Thead textAlign="left">Name</Thead>
						<Thead>Prep. time</Thead>
					</Trow>
					<Tbody>
						{search(recipes, searchParams, query).map((item, index, array) =>
							<Trow innerRef={(node) => index === array.length - 1 ? setLastElement(node) : null} key={item._id} onClick={() => navigate(`/studio/recipe/${item._id}`)} >
								<Tcol title="Name" borderRadius="10px 0 0 10px">
									<Div txtAlign="left">{item.name ?? "-"}</Div>
								</Tcol>
								<Tcol title="Prep. time" borderRadius="0 10px 10px 0">{formatPreparationTime(item.preparationTime)}</Tcol>
							</Trow>
						)}
					</Tbody>
				</Table>
			)}
			{loading && !create ? <Loader /> : null}
			{error && !loading && !create ? <EmptyState retry={fetchRecipes} /> : null}
			{create ? <RecipeCreate setCreateMode={setCreateMode} Patterns={Patterns} /> : null}
		</Page>
	);
};

export default RecipeList;
